import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { servicesAPI, vehiclesAPI, enumsAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Wrench,
  ChevronRight,
  Calendar,
  Gauge,
  DollarSign,
  Search,
  Filter,
  X,
} from "lucide-react";

const StatusBadge = ({ status }) => {
  if (status === "Overdue") {
    return (
      <span className="badge-overdue flex items-center gap-1">
        <span className="status-indicator status-overdue" />
        Overdue
      </span>
    );
  }
  if (status === "DueSoon") {
    return (
      <span className="badge-due-soon flex items-center gap-1">
        <span className="status-indicator status-due-soon" />
        Due Soon
      </span>
    );
  }
  return (
    <span className="badge-ok flex items-center gap-1">
      <span className="status-indicator status-ok" />
      OK
    </span>
  );
};

export default function MaintenanceList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [laborFilter, setLaborFilter] = useState("all");

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [vehicleRes, servicesRes, categoriesRes] = await Promise.all([
        vehiclesAPI.get(id),
        servicesAPI.list(id),
        enumsAPI.serviceCategories(),
      ]);
      setVehicle(vehicleRes.data);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Failed to load maintenance records");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Filtered services
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          service.title?.toLowerCase().includes(query) ||
          service.category?.toLowerCase().includes(query) ||
          service.notes?.toLowerCase().includes(query) ||
          service.parts_used?.toLowerCase().includes(query) ||
          service.fluids_used?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Category filter
      if (categoryFilter !== "all" && service.category !== categoryFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== "all" && service.status !== statusFilter) {
        return false;
      }

      // Labor filter
      if (laborFilter !== "all" && service.labor_type !== laborFilter) {
        return false;
      }

      return true;
    });
  }, [services, searchQuery, categoryFilter, statusFilter, laborFilter]);

  const clearFilters = () => {
    setSearchQuery("");
    setCategoryFilter("all");
    setStatusFilter("all");
    setLaborFilter("all");
  };

  const hasActiveFilters =
    searchQuery || categoryFilter !== "all" || statusFilter !== "all" || laborFilter !== "all";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!vehicle) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to={`/vehicles/${id}`}
                data-testid="back-to-dashboard-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />
                <div>
                  <h1 className="text-lg font-bold text-foreground">
                    Maintenance Log
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    {vehicle.nickname}
                  </p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => navigate(`/vehicles/${id}/services/new`)}
              data-testid="add-service-btn"
              className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Log Service
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {services.length === 0 ? (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-zinc-900 rounded-sm flex items-center justify-center mb-4">
                <Wrench className="w-8 h-8 text-zinc-600" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                No Service Records
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm">
                Start logging maintenance to track your vehicle's service history
              </p>
              <Button
                onClick={() => navigate(`/vehicles/${id}/services/new`)}
                data-testid="add-first-service-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Log First Service
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid gap-4 sm:grid-cols-3">
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Records</p>
                    <p className="text-xl font-mono font-bold">{services.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Spent</p>
                    <p className="text-xl font-mono font-bold">
                      ${services.reduce((sum, s) => sum + (s.total_cost || 0), 0).toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-sm flex items-center justify-center">
                    <Gauge className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Categories</p>
                    <p className="text-xl font-mono font-bold">
                      {new Set(services.map((s) => s.category)).size}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search & Filters */}
            <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search services..."
                      data-testid="search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-zinc-900 border-zinc-800"
                    />
                  </div>

                  {/* Category Filter */}
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger
                      data-testid="category-filter"
                      className="w-full sm:w-44 bg-zinc-900 border-zinc-800"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Status Filter */}
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger
                      data-testid="status-filter"
                      className="w-full sm:w-36 bg-zinc-900 border-zinc-800"
                    >
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="OK">OK</SelectItem>
                      <SelectItem value="DueSoon">Due Soon</SelectItem>
                      <SelectItem value="Overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Labor Filter */}
                  <Select value={laborFilter} onValueChange={setLaborFilter}>
                    <SelectTrigger
                      data-testid="labor-filter"
                      className="w-full sm:w-32 bg-zinc-900 border-zinc-800"
                    >
                      <SelectValue placeholder="Labor" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="DIY">DIY</SelectItem>
                      <SelectItem value="Shop">Shop</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      data-testid="clear-filters-btn"
                      className="border-zinc-800 hover:bg-zinc-800"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Filter Results Count */}
                {hasActiveFilters && (
                  <p className="text-sm text-muted-foreground mt-3">
                    Showing {filteredServices.length} of {services.length} records
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Services List */}
            <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
              <CardContent className="p-0">
                {filteredServices.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-muted-foreground">No services match your filters</p>
                    <Button
                      variant="link"
                      onClick={clearFilters}
                      className="text-primary mt-2"
                    >
                      Clear filters
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {filteredServices.map((service) => (
                      <Link
                        key={service.id}
                        to={`/vehicles/${id}/services/${service.id}/edit`}
                        data-testid={`service-item-${service.id}`}
                        className="flex items-center justify-between p-4 hover:bg-zinc-900/50 transition-colors group"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <StatusBadge status={service.status} />
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {service.title}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <span className="bg-zinc-800 px-2 py-0.5 rounded-sm">
                                {service.category}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(service.service_date + "T00:00:00").toLocaleDateString()}
                              </span>
                              {service.service_mileage > 0 && (
                                <span className="flex items-center gap-1 font-mono">
                                  <Gauge className="w-3 h-3" />
                                  {service.service_mileage.toLocaleString()} mi
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {service.total_cost > 0 && (
                            <span className="text-sm font-mono text-muted-foreground">
                              ${service.total_cost.toLocaleString()}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-sm ${
                            service.labor_type === "DIY" 
                              ? "bg-green-500/10 text-green-500" 
                              : "bg-blue-500/10 text-blue-500"
                          }`}>
                            {service.labor_type}
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
